import { Ref } from 'vue';
import {
  AllAttributesType,
  allAttrsEnum,
  MetricsType,
  WidgetDisplayOptions,
} from '@/services/api/resources/analytics-builder/constants';
import { FormItemFilterRecord, ReportWidget } from '@/services/api/resources/analytics-builder/model';
import { useAttributes } from './useAttributes';
import { useAnalyticsMap } from '@/ui/concerns/analytics/useAnalyticsMap';

export const useDisplay = () => {
  const { allowedDisplaysByResponseType } = useAttributes();
  const { getEnabledDisplays } = useAnalyticsMap();

  const displayOptions: Array<{ value: WidgetDisplayOptions; icon: string }> = [
    { value: WidgetDisplayOptions.singleNumber, icon: 'hashtag' },
    { value: WidgetDisplayOptions.barChart, icon: 'chart-bar' },
    { value: WidgetDisplayOptions.lineChart, icon: 'chart-line' },
    { value: WidgetDisplayOptions.pieChart, icon: 'chart-pie' },
    { value: WidgetDisplayOptions.table, icon: 'table' },
  ];

  const displayOptionsArray = () => Object.entries(WidgetDisplayOptions)
    .map(([key, value]) => ({ key, value }));

  const getSelectedDisplayOption = (widget: ReportWidget): WidgetDisplayOptions | null => {
    switch (true) {
      case widget.singleNumberDisplay:
        return WidgetDisplayOptions.singleNumber;
      case widget.barChartDisplay:
        return WidgetDisplayOptions.barChart;
      case widget.lineChartDisplay:
        return WidgetDisplayOptions.lineChart;
      case widget.pieChartDisplay:
        return WidgetDisplayOptions.pieChart;
      case widget.tableDisplay:
        return WidgetDisplayOptions.table;
      default:
        return null;
    }
  };

  const shouldDisableDisplayForResponseAttribute = (
    option: WidgetDisplayOptions,
    formItemDetails: Ref<FormItemFilterRecord | undefined>,
  ) => {
    if (!formItemDetails.value) {
      return false;
    }

    return !(allowedDisplaysByResponseType[formItemDetails.value.type] || []).includes(option);
  };

  const shouldDisableDisplay = (
    option: WidgetDisplayOptions,
    selectedMetric: Ref<MetricsType | null>,
    selectedAttribute: Ref<AllAttributesType | null>,
    formItemDetails: Ref<FormItemFilterRecord | undefined>,
  ) => {
    if (!selectedAttribute.value || !selectedMetric.value) {
      return true;
    }

    if (selectedAttribute.value === allAttrsEnum.response) {
      return shouldDisableDisplayForResponseAttribute(option, formItemDetails);
    }

    const enabledDisplays = getEnabledDisplays(selectedMetric.value, selectedAttribute.value);

    return !enabledDisplays.includes(option);
  };

  const isChart = (display: WidgetDisplayOptions) => [
    WidgetDisplayOptions.lineChart,
    WidgetDisplayOptions.barChart,
    WidgetDisplayOptions.pieChart,
  ].includes(display);

  return {
    displayOptions,
    displayOptionsArray,
    getSelectedDisplayOption,
    shouldDisableDisplay,
    isChart,
  };
};
